<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
        </div>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.selfRegistration') }}</h2>
            <p class="additional-screens-disclaimer">{{ $LANG.Get('texts.registrationSettingsDisclaimer') }}</p>

            <NevsCheckbox v-model="settings.enabled" :label="$LANG.Get('labels.selfRegistrationEnabled')"></NevsCheckbox>

            <template v-if="settings.enabled">
                <NevsTextField v-model="settings.link" :error="validation.link"
                               :label="$LANG.Get('labels.selfRegistrationLink') + ':'"></NevsTextField>
                <p v-if="settings.link !== ''" class="additional-screens-url">{{ formUrl }}</p>
                <NevsTextField v-model="settings.password"
                               :label="$LANG.Get('labels.selfRegistrationPassword') + ':'"></NevsTextField>

                <table class="additional-screens-table">
                    <thead>
                        <tr>
                            <th>{{ $LANG.Get('labels.field') }}</th>
                            <th style="width: 120px; text-align: center;">{{ $LANG.Get('labels.fieldAvailable') }}</th>
                            <th style="width: 120px; text-align: center;">{{ $LANG.Get('labels.fieldMandatory') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="field in settings.fields" :key="field.field">
                            <td>{{ fieldLabel(field.field) }}</td>
                            <td style="text-align: center;">
                                <NevsCheckbox :width="'auto'" :readonly="field.locked" v-model="field.available"
                                              @update:modelValue="onAvailableChange(field)"></NevsCheckbox>
                            </td>
                            <td style="text-align: center;">
                                <NevsCheckbox :width="'auto'" :readonly="field.locked || !field.available"
                                              v-model="field.mandatory"></NevsCheckbox>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </NevsCard>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.welcomeScreen') }}</h2>
            <p class="additional-screens-disclaimer">{{ $LANG.Get('texts.welcomeScreenDisclaimer') }}</p>

            <NevsCheckbox v-model="settings.welcome_screen_enabled"
                          :label="$LANG.Get('labels.welcomeScreenEnabled')"></NevsCheckbox>

            <template v-if="settings.welcome_screen_enabled">
                <NevsTextField v-model="settings.welcome_screen_link" :error="validation.welcome_screen_link"
                               :label="$LANG.Get('labels.selfRegistrationLink') + ':'"></NevsTextField>
                <p v-if="settings.welcome_screen_link !== ''" class="additional-screens-url">{{ welcomeUrl }}</p>
                <NevsTextField v-model="settings.welcome_screen_password"
                               :label="$LANG.Get('labels.selfRegistrationPassword') + ':'"></NevsTextField>
                <NevsNumberField v-model="settings.welcome_screen_timer" :decimal-places="0"
                                 :label="$LANG.Get('labels.welcomeScreenTimer') + ':'"></NevsNumberField>
                <NevsCheckbox v-model="settings.welcome_screen_allow_without_membership"
                              :label="$LANG.Get('labels.welcomeScreenAllowWithoutMembership')"></NevsCheckbox>
            </template>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";

export default {
    name: "AdditionalScreens",
    components: {NevsNumberField, NevsTextField, NevsCheckbox, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            settings: {
                enabled: false,
                link: '',
                password: '',
                fields: [],
                welcome_screen_enabled: false,
                welcome_screen_link: '',
                welcome_screen_password: '',
                welcome_screen_timer: 3,
                welcome_screen_allow_without_membership: false
            },
            validation: {
                link: '',
                welcome_screen_link: ''
            },
            // Maps a registrations column to its translation key.
            fieldLabels: {
                first_name: 'fields.firstName',
                last_name: 'fields.lastName',
                oib: 'fields.oib',
                gender_id: 'fields.gender',
                date_of_birth: 'fields.dateOfBirth',
                email: 'fields.email',
                phone: 'fields.phone',
                address: 'fields.address',
                postal_code: 'fields.postalCode',
                city: 'fields.city',
                country: 'fields.country'
            }
        }
    },
    computed: {
        formUrl() {
            return window.location.origin + '/self-register/' + this.settings.link;
        },
        welcomeUrl() {
            return window.location.origin + '/welcome-screen/' + this.settings.welcome_screen_link;
        }
    },
    methods: {
        fieldLabel(field) {
            return this.fieldLabels[field] ? this.$LANG.Get(this.fieldLabels[field]) : field;
        },
        onAvailableChange(field) {
            // A field that is not available cannot be mandatory.
            if (!field.available) {
                field.mandatory = false;
            }
        },
        saveClick() {
            this.validation.link = '';
            this.validation.welcome_screen_link = '';

            // Both public links go straight into their URLs, so an enabled screen must have a link and it may
            // only contain letters, numbers and dashes.
            if (this.settings.enabled) {
                if (this.settings.link.trim() === '') {
                    this.validation.link = this.$LANG.Get('labels.mandatoryField');
                    return;
                }
                if (!/^[A-Za-z0-9-]+$/.test(this.settings.link.trim())) {
                    this.validation.link = this.$LANG.Get('labels.selfRegistrationLinkInvalid');
                    return;
                }
            }
            if (this.settings.welcome_screen_enabled) {
                if (this.settings.welcome_screen_link.trim() === '') {
                    this.validation.welcome_screen_link = this.$LANG.Get('labels.mandatoryField');
                    return;
                }
                if (!/^[A-Za-z0-9-]+$/.test(this.settings.welcome_screen_link.trim())) {
                    this.validation.welcome_screen_link = this.$LANG.Get('labels.selfRegistrationLinkInvalid');
                    return;
                }
            }

            // The column is an int and the API validates it strictly, so a cleared or nonsensical countdown falls
            // back to the default rather than sending a NaN the API would reject.
            let welcomeTimer = parseInt(this.settings.welcome_screen_timer);
            this.settings.welcome_screen_timer = (isNaN(welcomeTimer) || welcomeTimer <= 0) ? 3 : welcomeTimer;

            let vm = this;
            this.$API.APICall('put', 'registration-settings', this.settings, (data, success) => {
                if (success) {
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                } else if (data && data.link === 'taken') {
                    vm.validation.link = vm.$LANG.Get('labels.selfRegistrationLinkTaken');
                } else if (data && data.link === 'invalid') {
                    vm.validation.link = vm.$LANG.Get('labels.selfRegistrationLinkInvalid');
                } else if (data && data.welcome_screen_link === 'taken') {
                    vm.validation.welcome_screen_link = vm.$LANG.Get('labels.selfRegistrationLinkTaken');
                } else if (data && data.welcome_screen_link === 'invalid') {
                    vm.validation.welcome_screen_link = vm.$LANG.Get('labels.selfRegistrationLinkInvalid');
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'settings');
        this.$store.commit('selectSubMenu', 'additionalScreens');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.settings'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.additionalScreens'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'registration-settings', {}, (data, success) => {
            if (success) {
                vm.settings = {
                    enabled: data.enabled,
                    link: data.link,
                    password: data.password,
                    fields: data.fields,
                    welcome_screen_enabled: data.welcome_screen_enabled,
                    welcome_screen_link: data.welcome_screen_link,
                    welcome_screen_password: data.welcome_screen_password,
                    welcome_screen_timer: data.welcome_screen_timer,
                    welcome_screen_allow_without_membership: data.welcome_screen_allow_without_membership
                };
                vm.loaded = true;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
        });
    }
}
</script>

<style scoped>
.form {
    max-width: 600px;
    padding: 15px;
}

.form + .form {
    margin-top: 20px;
}

.additional-screens-disclaimer {
    color: #5e6278;
    margin: 0 0 15px;
}

.additional-screens-url {
    margin: -5px 0 10px;
    font-size: 13px;
    color: #2f6f4e;
    word-break: break-all;
}

.additional-screens-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
}

.additional-screens-table th {
    text-align: left;
    font-size: 12px;
    padding: 8px 5px;
    border-bottom: 1px solid grey;
}

.additional-screens-table td {
    padding: 8px 5px;
    border-bottom: 1px solid #e3e7ee;
    vertical-align: middle;
}
</style>
